import { colors, ColorWeight } from '@theme/foundations/colors';
import { targetColor } from '@utils/map/map.constants';
import { sensorFamilyFromSensorType } from '@utils/sensors/configuration.constants';
import { getDataStatusInfoIcon, getUniqueSensorFamilies } from '@utils/target.utils';
import { FunctionComponent, SVGProps } from 'react';

import { ActionEnum } from '@/types/commons/commons.types';
import { ProcedureStep } from '@/types/config/procedure.types';
import {
  AlertLevelEnum,
  AlertType,
  AlertTypeEnum,
  criticalAlertLevel,
  DroneAlertLevelEnum,
  DroneNotificationLight,
  ManualAlert,
  NotificationFilterEnum,
  NotificationSortEnum,
  NotificationType,
  NotificationTypeEnum,
  SegmentAlertLevelEnum,
  SegmentNotification,
  SensorAlertLevelEnum,
  SensorNotification,
  SpaceViolationAlert,
} from '@/types/data/data.types';
import { ClassificationEnum } from '@/types/sensor/classification.types';
import { ReliabilityEnum } from '@/types/sensor/identification.types';
import { SensorAction, SensorFamilyEnum } from '@/types/sensor/sensor.types';
import {
  ConnectorStatusEnum,
  EffectiveSensorStatusEnum,
  HealthStatusEnum,
  SensorTypeEnum,
  SpecificStateEnum,
} from '@/types/sensor/sensors';
import { DataInfoSizeEnum, DataInfoStatusEnum } from '@/types/target.types';
import { DroneClearanceEnum } from '@/types/utm/drone.types';

export function getWorstNotificationLevel(
  { currentWorstAlertLevel, lastAlertLevel, worstAlertLevel }: NotificationType,
  option?: { isArchived?: boolean; isToast?: boolean },
) {
  const { isArchived, isToast } = { isArchived: false, isToast: false, ...option };
  if (isArchived) {
    return worstAlertLevel;
  } else if (isToast) {
    return lastAlertLevel;
  } else {
    return currentWorstAlertLevel;
  }
}

export function isDroneNotification(notification: NotificationType): notification is DroneNotificationLight {
  return notification.type === NotificationTypeEnum.DRONE;
}

export function isSegmentNotification(notification: NotificationType): notification is SegmentNotification {
  return notification.type === NotificationTypeEnum.SEGMENT;
}

export function isSensorNotification(notification: NotificationType): notification is SensorNotification {
  return notification.type === NotificationTypeEnum.SENSOR;
}

export function isLadNotification(notification: NotificationType) {
  return isDroneNotification(notification);
}

export function isPerimeterNotification(
  notification: NotificationType,
  isArchived = false,
): notification is SegmentNotification {
  const level = getWorstNotificationLevel(notification, { isArchived });
  return isSegmentNotification(notification) && level === SegmentAlertLevelEnum.PERIMETER_EFFRACTION;
}

export function isLadTechnicalNotification(notification: NotificationType) {
  return isSensorNotification(notification) && notification.sensorType !== SensorTypeEnum.AUTOMATON;
}

export function isPerimeterTechnicalNotification(notification: NotificationType) {
  return isSensorNotification(notification) && notification.sensorType === SensorTypeEnum.AUTOMATON;
}

function getPriorityValue(
  notification: NotificationType,
): EffectiveSensorStatusEnum | ClassificationEnum | SpecificStateEnum.EFFRACTION | null {
  switch (notification.type) {
    case NotificationTypeEnum.DRONE:
      return notification.classification;
    case NotificationTypeEnum.SENSOR:
      return notification.sensorStatus;
    case NotificationTypeEnum.SEGMENT:
      return SpecificStateEnum.EFFRACTION;
  }
}

// Sort by endTime: Non-ended comes first
export function sortOnGoingFirst(endTime1: string | null, endTime2: string | null): number {
  if (endTime1 === null && endTime2 !== null) {
    return -1;
  }
  if (endTime1 !== null && endTime2 === null) {
    return 1;
  }
  return 0;
}

// Sort by reliability: SURE comes first, then UNSURE, then non drone
export function sortNotificationByReliability(a: NotificationType, b: NotificationType): number {
  const aIsDrone = isDroneNotification(a);
  const bIsDrone = isDroneNotification(b);

  if (aIsDrone && !bIsDrone) {
    return -1;
  }
  if (!aIsDrone && bIsDrone) {
    return 1;
  }

  if (!aIsDrone && !bIsDrone) {
    return 0;
  }

  const droneA = a as DroneNotificationLight;
  const droneB = b as DroneNotificationLight;

  if (droneA.reliability !== droneB.reliability) {
    return droneA.reliability === ReliabilityEnum.SURE ? -1 : 1;
  }

  return 0;
}

// Sort by startTime: Most recent first
export function sortByStartTime(a: string, b: string): number {
  const dateA = new Date(a).getTime();
  const dateB = new Date(b).getTime();
  return dateB - dateA;
}

export function sortNotificationByDanger(notification1: NotificationType, notification2: NotificationType) {
  const priorityOrder = [
    ClassificationEnum.ENEMY,
    SpecificStateEnum.EFFRACTION,
    ConnectorStatusEnum.UNREACHABLE,
    ClassificationEnum.SUSPECT,
    HealthStatusEnum.KO,
    HealthStatusEnum.DEGRADED,
    ClassificationEnum.WATCH,
    ClassificationEnum.UNKNOWN,
    HealthStatusEnum.HEALTHY,
    ClassificationEnum.FRIEND,
    ClassificationEnum.UNCLASSIFIED,
    null,
  ];

  const priority1 = getPriorityValue(notification1);
  const priority2 = getPriorityValue(notification2);

  return priorityOrder.indexOf(priority1) - priorityOrder.indexOf(priority2);
}

export function sortNotifications(
  notifications: NotificationType[],
  selectedSort: NotificationSortEnum,
): NotificationType[] {
  return notifications.toSorted((a, b) => {
    return (
      sortOnGoingFirst(a.endTime, b.endTime) ||
      sortNotificationByReliability(a, b) ||
      (() => {
        switch (selectedSort) {
          case NotificationSortEnum.START_TIME:
            return sortByStartTime(a.startTime, b.startTime);
          case NotificationSortEnum.DANGER:
            return sortNotificationByDanger(a, b);
          default:
            return 0;
        }
      })()
    );
  });
}

export function filterNotifications(
  notifications: NotificationType[],
  filters: NotificationFilterEnum[],
): NotificationType[] {
  return notifications.filter((notification) => {
    const isLAD = filters.includes(NotificationFilterEnum.LAD) && isLadNotification(notification);
    const isPerimeter = filters.includes(NotificationFilterEnum.PERIMETER) && isPerimeterNotification(notification);
    const isLadTechnical =
      filters.includes(NotificationFilterEnum.LAD_TECHNICAL) && isLadTechnicalNotification(notification);
    const isPerimeterTechnical =
      filters.includes(NotificationFilterEnum.PERIMETER_TECHNICAL) && isPerimeterTechnicalNotification(notification);

    return isLAD || isPerimeter || isLadTechnical || isPerimeterTechnical;
  });
}

export function sortAlerts(alerts: AlertType[]) {
  return alerts.toSorted((a, b) => {
    return sortOnGoingFirst(a.endTime, b.endTime) || sortByStartTime(a.startTime, b.startTime);
  });
}

export function isSpaceViolationAlert(alert: AlertType): alert is SpaceViolationAlert {
  return alert.type === AlertTypeEnum.SPACE_VIOLATION;
}

export function isManualAlert(alert: AlertType): alert is ManualAlert {
  return alert.type === AlertTypeEnum.MANUAL;
}

export function isCriticalNotification(
  notification: NotificationType,
  opt?: { isArchived?: boolean; isToast?: boolean },
) {
  const level = getWorstNotificationLevel(notification, opt);
  return level !== null && criticalAlertLevel.includes(level);
}

export function getAlertLevelColor(level: AlertLevelEnum | null, weight: ColorWeight = 500) {
  if (level === null) {
    return targetColor[ClassificationEnum.UNKNOWN];
  }
  switch (level) {
    case DroneAlertLevelEnum.DRONE_INFO:
    case SensorAlertLevelEnum.SENSOR_WARNING_NON_DETECTION:
      return `alertLo.${weight}`;
    case DroneAlertLevelEnum.DRONE_WARNING:
    case SensorAlertLevelEnum.SENSOR_FAILURE:
    case SensorAlertLevelEnum.SENSOR_NON_DETECTION:
      return `alertMid.${weight}`;
    case SegmentAlertLevelEnum.PERIMETER_EFFRACTION:
    case DroneAlertLevelEnum.DRONE_CRITICAL:
      return `alertHi.${weight}`;
    case SensorAlertLevelEnum.SENSOR_DISCONNECTED:
      return `alertHs.${weight}`;
  }
}

export function getNotificationColor(
  notification: NotificationType,
  opt?: {
    weight?: ColorWeight;
    isArchived?: boolean;
    isToast?: boolean;
  },
) {
  const { weight, ...option } = {
    weight: 500 as ColorWeight,
    isArchived: false,
    isToast: false,
    ...opt,
  };
  const level = getWorstNotificationLevel(notification, option);
  return getAlertLevelColor(level, weight);
}

function getClearanceDataInfoStatus(clearance: DroneClearanceEnum | null): DataInfoStatusEnum {
  switch (clearance) {
    case DroneClearanceEnum.CLEARANCE_WITHIN_FLIGHTPLAN:
    case DroneClearanceEnum.FULL_CLEARANCE:
    case DroneClearanceEnum.FULL_CLEARANCE_WITHIN_FLIGHTPLAN:
      return DataInfoStatusEnum.OK;
    case DroneClearanceEnum.NO_CLEARANCE:
      return DataInfoStatusEnum.HIGH;
    default:
      return DataInfoStatusEnum.RAS;
  }
}

export function getClearanceColor(clearance: DroneClearanceEnum | null) {
  switch (clearance) {
    case DroneClearanceEnum.CLEARANCE_WITHIN_FLIGHTPLAN:
    case DroneClearanceEnum.FULL_CLEARANCE:
    case DroneClearanceEnum.FULL_CLEARANCE_WITHIN_FLIGHTPLAN:
      return colors.green.DEFAULT.value;
    case DroneClearanceEnum.NO_CLEARANCE:
      return colors.red.DEFAULT.value;
    default:
      return colors.neutral['300'].value;
  }
}

export function getClearanceStatusIcon(
  clearance: DroneClearanceEnum | null,
  size: DataInfoSizeEnum,
): FunctionComponent<SVGProps<SVGSVGElement>> {
  const dataStatus = getClearanceDataInfoStatus(clearance);
  return getDataStatusInfoIcon(dataStatus, size);
}

export function getNotificationName(notification: NotificationType) {
  switch (notification.type) {
    case NotificationTypeEnum.SENSOR:
      return notification.sensorName;
    case NotificationTypeEnum.SEGMENT:
      return notification.segmentName;
    case NotificationTypeEnum.DRONE:
      return notification.displayId;
  }
}

export function getAuthoritiesForNotificationManagement(notification: NotificationType | undefined): SensorAction[] {
  if (notification === undefined) {
    return [];
  }

  switch (notification.type) {
    case NotificationTypeEnum.SENSOR:
      return [
        {
          action: ActionEnum.MAINTAIN,
          sensorFamily: sensorFamilyFromSensorType[notification.sensorType],
        },
      ];
    case NotificationTypeEnum.DRONE:
      return getUniqueSensorFamilies(notification.trackIds).map((sensorFamily) => ({
        action: ActionEnum.MANAGE_TARGET,
        sensorFamily: sensorFamily,
      }));
    case NotificationTypeEnum.SEGMENT:
      return [
        {
          action:
            //For now Segment have only a single alert level
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            notification.worstAlertLevel === SegmentAlertLevelEnum.PERIMETER_EFFRACTION
              ? ActionEnum.MANAGE_TARGET
              : ActionEnum.MAINTAIN,
          sensorFamily: SensorFamilyEnum.AUTOMATON,
        },
      ];
  }
}

export function canManageNotification(userActions: Set<SensorAction>, notification: NotificationType) {
  const actions = getAuthoritiesForNotificationManagement(notification);
  return Array.from(userActions).some((usa) =>
    actions.some((psa) => usa.action === psa.action && usa.sensorFamily === psa.sensorFamily),
  );
}

export function findStepByRank(steps: ProcedureStep[], rank: number | null): ProcedureStep | null {
  return steps.find((step) => step.rank === rank) ?? null;
}

function getNotificationTypeRank(notification: NotificationType): number {
  if (isLadNotification(notification)) {
    return 0;
  } else if (isPerimeterNotification(notification)) {
    return 1;
  } else if (isLadTechnicalNotification(notification)) {
    return 2;
  } else {
    return 3;
  }
}

export function sortNotificationByType(a: NotificationType, b: NotificationType): number {
  return getNotificationTypeRank(a) - getNotificationTypeRank(b);
}

function compareNotifications(a: NotificationType, b: NotificationType): number {
  return sortNotificationByType(a, b) || sortNotificationByDanger(a, b) || sortNotificationByReliability(a, b);
}

export function getHighestPriorityNotification(notifications: NotificationType[]): NotificationType | null {
  if (notifications.length === 0) {
    return null;
  }
  return notifications.reduce((best, current) => {
    return compareNotifications(current, best) < 0 ? current : best;
  }, notifications[0]);
}

//TODO : Add missing sound files
export function getNotificationSound(notification: NotificationType): string {
  if (isPerimeterNotification(notification)) {
    return '/audio/perimAlarm.mp3';
  } else if (isLadTechnicalNotification(notification) || isPerimeterTechnicalNotification(notification)) {
    return '/audio/technicalAlarm.mp3';
  }
  if (isCriticalNotification(notification)) {
    return isDroneNotification(notification) && notification.reliability === ReliabilityEnum.SURE
      ? '/audio/ladSureAlarm.mp3'
      : '/audio/ladUnsureAlarm.mp3';
  }
  if (getWorstNotificationLevel(notification) === DroneAlertLevelEnum.DRONE_WARNING) {
    return isDroneNotification(notification) && notification.reliability === ReliabilityEnum.SURE
      ? '/audio/ladSureAlarm.mp3'
      : '/audio/ladUnsureAlarm.mp3';
  }
  return isDroneNotification(notification) && notification.reliability === ReliabilityEnum.SURE
    ? '/audio/ladSureAlarm.mp3'
    : '/audio/ladUnsureAlarm.mp3';
}

export function getWorstAlertLevelColor(alerts: AlertType[]): string {
  if (alerts.length === 0) {
    return colors.neutral['300'].value;
  }
  const priority = [
    SegmentAlertLevelEnum.PERIMETER_EFFRACTION,
    DroneAlertLevelEnum.DRONE_CRITICAL,
    SensorAlertLevelEnum.SENSOR_DISCONNECTED,
    DroneAlertLevelEnum.DRONE_WARNING,
    SensorAlertLevelEnum.SENSOR_FAILURE,
    SensorAlertLevelEnum.SENSOR_NON_DETECTION,
    SensorAlertLevelEnum.SENSOR_WARNING_NON_DETECTION,
    DroneAlertLevelEnum.DRONE_INFO,
  ];
  const priorityAlert = alerts.toSorted((a, b) => priority.indexOf(a.level) - priority.indexOf(b.level))[0];
  return getAlertLevelColor(priorityAlert.level);
}
